import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ViewWrapper } from "../components/ViewWrapper";

type ServiceItem = {
  name: string;
  price: string;
  time: string;
  description: string;
};

type ServiceCategory = {
  name: string;
  note?: string;
  items: ServiceItem[];
};

const CATEGORIES: ServiceCategory[] = [
  {
    name: "Apostille",
    items: [
      {
        name: "Apostille",
        price: "$250",
        time: "5–15 business days",
        description:
          "Authentication of a US-issued document for international use under the Hague Convention.",
      },
    ],
  },
  {
    name: "Article Amendment",
    items: [
      {
        name: "Article Amendment",
        price: "$135",
        time: "5–15 business days",
        description:
          "Amend the articles of organization on file with the Secretary of State.",
      },
    ],
  },
  {
    name: "Attorney Products",
    note: "Only order with explicit attorney instructions",
    items: [
      {
        name: "Assignment of Interest",
        price: "$100",
        time: "1–3 business days",
        description: "Transfer member or manager interest in the LLC.",
      },
      {
        name: "NDA",
        price: "$100",
        time: "1–3 business days",
        description: "Mutual or one-way non-disclosure agreement.",
      },
      {
        name: "Independent Contractor Agreement",
        price: "$100",
        time: "1–3 business days",
        description: "Standard agreement for engaging independent contractors.",
      },
    ],
  },
  {
    name: "Certificate of Good Standing",
    items: [
      {
        name: "Certificate of Good Standing",
        price: "$35",
        time: "1–5 business days",
        description:
          "Official state-issued document confirming your LLC is in compliance.",
      },
    ],
  },
  {
    name: "Certificate of Incumbency",
    items: [
      {
        name: "Standard / You formed",
        price: "$225",
        time: "1–3 business days",
        description:
          "Standard incumbency certificate for an LLC you formed yourself.",
      },
      {
        name: "Standard / We formed",
        price: "$175",
        time: "1–3 business days",
        description: "Standard incumbency certificate for an LLC we formed.",
      },
      {
        name: "Custom / You formed",
        price: "$350",
        time: "1–5 business days",
        description:
          "Customized incumbency certificate for an LLC you formed yourself.",
      },
      {
        name: "Custom / We formed",
        price: "$225",
        time: "1–5 business days",
        description: "Customized incumbency certificate for an LLC we formed.",
      },
    ],
  },
  {
    name: "Certified Copies of Articles",
    items: [
      {
        name: "Certified Copies of Articles",
        price: "$50",
        time: "5–15 business days",
        description: "State-certified copies of articles of organization.",
      },
    ],
  },
  {
    name: "Convert Entity",
    items: [
      {
        name: "LLC ↔ Close LLC",
        price: "$175",
        time: "5–15 business days",
        description:
          "Convert a standard LLC to a Close LLC or vice versa (Wyoming and select states).",
      },
      {
        name: "LLC ↔ Corporation",
        price: "$299",
        time: "5–15 business days",
        description:
          "Convert an LLC to a Corporation or vice versa via state conversion filing.",
      },
    ],
  },
  {
    name: "Corporate Binder and Seal",
    items: [
      {
        name: "Corporate Binder and Seal",
        price: "$200",
        time: "3–7 business days",
        description: "Physical corporate kit with binder and embossing seal.",
      },
    ],
  },
  {
    name: "DBA / Trade Name",
    items: [
      {
        name: "Cancellation",
        price: "$100",
        time: "5–15 business days",
        description: "Cancel an existing DBA / trade name with the state.",
      },
      {
        name: "Reassign Wyoming DBA",
        price: "$100",
        time: "5–15 business days",
        description: "Reassign a Wyoming DBA to a different entity.",
      },
      {
        name: "DBA / Trade Name",
        price: "$175",
        time: "5–15 business days",
        description: "File a new DBA / trade name registration.",
      },
    ],
  },
  {
    name: "EIN",
    items: [
      {
        name: "EIN",
        price: "$75",
        time: "Online same day / fax 3–5d / mail 20–30d",
        description: "Federal Employer Identification Number from the IRS.",
      },
    ],
  },
  {
    name: "IRS Additional Services",
    items: [
      {
        name: "Change tax election",
        price: "$100",
        time: "30–60 days",
        description: "Change federal tax election (Form 8832).",
      },
      {
        name: "Update responsible party + address",
        price: "$50",
        time: "20–40 days",
        description: "Update both responsible party and business address with IRS.",
      },
      {
        name: "Update responsible party",
        price: "$50",
        time: "20–40 days",
        description: "Form 8822-B responsible party update.",
      },
      {
        name: "Change company name",
        price: "$100",
        time: "20–40 days",
        description: "Update company name with the IRS after a state amendment.",
      },
      {
        name: "Update business address",
        price: "$50",
        time: "20–40 days",
        description: "Update business address of record with IRS.",
      },
      {
        name: "S-Corp election",
        price: "$150",
        time: "30–60 days",
        description: "File Form 2553 to elect S-Corp tax treatment.",
      },
    ],
  },
  {
    name: "LLC Docs – Add/Remove Members/Managers",
    items: [
      {
        name: "Company buyout",
        price: "$100",
        time: "1–3 business days",
        description: "Document a full company buyout.",
      },
      {
        name: "Member buyout",
        price: "$100",
        time: "1–3 business days",
        description: "Document the buyout of a single member.",
      },
      {
        name: "Appoint manager",
        price: "$100",
        time: "1–3 business days",
        description: "Resolution to appoint a new manager.",
      },
      {
        name: "Remove manager",
        price: "$100",
        time: "1–3 business days",
        description: "Resolution to remove a manager.",
      },
      {
        name: "New member",
        price: "$100",
        time: "1–3 business days",
        description: "Admit a new member to the LLC.",
      },
      {
        name: "Updated operating agreement",
        price: "$50",
        time: "1–3 business days",
        description: "Refresh of the operating agreement after member changes.",
      },
      {
        name: "Custom operating agreement (Wyoming only)",
        price: "$200",
        time: "3–7 business days",
        description: "Fully custom operating agreement for Wyoming LLCs.",
      },
    ],
  },
  {
    name: "LLC Docs – Meeting Minutes",
    items: [
      {
        name: "Custom annual minutes",
        price: "$50",
        time: "1–3 business days",
        description: "Customized annual meeting minutes.",
      },
      {
        name: "Special meeting minutes",
        price: "$25",
        time: "1–3 business days",
        description: "Minutes for ad-hoc special meetings.",
      },
      {
        name: "Contribution minutes",
        price: "$20",
        time: "1–3 business days",
        description: "Document a capital contribution.",
      },
      {
        name: "Distribution minutes",
        price: "$20",
        time: "1–3 business days",
        description: "Document a distribution to members.",
      },
      {
        name: "Digital asset assignment",
        price: "$50",
        time: "1–3 business days",
        description: "Assign digital assets (domains, IP) to the LLC.",
      },
    ],
  },
  {
    name: "Name Change",
    items: [
      {
        name: "Name Change",
        price: "$185",
        time: "5–15 business days",
        description: "Amend articles to change the LLC's legal name.",
      },
    ],
  },
];

export function ServicesView() {
  const singles = CATEGORIES.filter((c) => c.items.length === 1);
  const multi = CATEGORIES.filter((c) => c.items.length > 1);

  return (
    <ViewWrapper>
      <div style={{ padding: "0 32px 40px", maxWidth: 1280, margin: "0 auto" }}>
        <h2
          style={{
            margin: "0 0 14px",
            fontSize: 22,
            fontWeight: 700,
          }}
        >
          One-time Services
        </h2>
        <div className="services-boxes">
          {singles.map((c) => (
            <ServiceBox key={c.name} category={c} />
          ))}
        </div>

        <div style={{ height: 32 }} />

        <h2
          style={{
            margin: "0 0 14px",
            fontSize: 22,
            fontWeight: 700,
          }}
        >
          Service Categories
        </h2>
        <div className="services-cards">
          {multi.map((c) => (
            <ServiceCard key={c.name} category={c} />
          ))}
        </div>
      </div>
    </ViewWrapper>
  );
}

function ServiceBox({ category }: { category: ServiceCategory }) {
  const item = category.items[0];
  const [expanded, setExpanded] = useState(false);

  return (
    <button
      onClick={() => setExpanded((x) => !x)}
      style={{
        textAlign: "left",
        background: "rgba(8,10,18,0.6)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 14,
        padding: "12px 14px",
        color: "#fff",
        fontFamily: "inherit",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>
        {category.name}
      </div>
      <div style={{ fontSize: 18, fontWeight: 800 }}>{item.price}</div>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: "#818cf8", fontWeight: 600 }}>
            {item.time}
          </div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
            {item.description}
          </div>
        </div>
      )}
    </button>
  );
}

function ServiceCard({ category }: { category: ServiceCategory }) {
  return (
    <div
      style={{
        background: "rgba(8,10,18,0.6)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 14,
        padding: "16px 16px 6px",
        color: "#fff",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
        {category.name}
      </div>
      {category.note && (
        <div
          style={{
            fontSize: 11,
            color: "#fde68a",
            opacity: 0.9,
            marginBottom: 8,
          }}
        >
          ⚠ {category.note}
        </div>
      )}
      {category.items.map((i) => (
        <ItemRow key={i.name} item={i} />
      ))}
    </div>
  );
}

function ItemRow({ item }: { item: ServiceItem }) {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [showTip, setShowTip] = useState(false);
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(
    null
  );

  function openTip() {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const tipW = 240;
    let left = r.left + r.width / 2 - tipW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - tipW - 8));
    const top = r.bottom + 6;
    setTipPos({ left, top });
    setShowTip(true);
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 0",
        borderTop: "1px solid rgba(255,255,255,0.08)",
        gap: 10,
      }}
    >
      <div
        style={{ flex: 1, fontSize: 13, lineHeight: 1.35, minWidth: 0 }}
      >
        {item.name}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          flex: "0 0 auto",
        }}
      >
        {item.price}
      </div>
      <button
        ref={btnRef}
        type="button"
        onClick={openTip}
        onMouseEnter={openTip}
        onMouseLeave={() => setShowTip(false)}
        onBlur={() => setShowTip(false)}
        aria-label="More info"
        style={{
          width: 20,
          height: 20,
          borderRadius: 999,
          border: "1px solid rgba(255,255,255,0.22)",
          background: "rgba(8,10,18,0.85)",
          color: "#fff",
          fontSize: 11,
          padding: 0,
          flex: "0 0 auto",
        }}
      >
        ⓘ
      </button>

      {showTip &&
        tipPos &&
        createPortal(
          <Tooltip item={item} pos={tipPos} />,
          document.body
        )}
    </div>
  );
}

function Tooltip({
  item,
  pos,
}: {
  item: ServiceItem;
  pos: { left: number; top: number };
}) {
  return (
    <div
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        width: 240,
        zIndex: 200,
        background: "rgba(10,12,20,0.92)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 10,
        padding: "10px 12px",
        boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
        pointerEvents: "none",
        color: "#fff",
        fontSize: 12,
        lineHeight: 1.45,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div style={{ color: "#818cf8", fontWeight: 700, marginBottom: 4 }}>
        {item.time}
      </div>
      <div>{item.description}</div>
    </div>
  );
}

